// /frontend/src/pages/dashboard/post/previews/InstagramPreview.js

import React, { useState } from 'react'

const VIDEO_EXT   = ['.mp4', '.mov', '.webm', '.avi', '.m4v']
const PLACEHOLDER = 'https://placehold.co/32x32/E4405F/FFFFFF?text=IG'

const isVideoUrl = (url) =>
  url && VIDEO_EXT.some((ext) => url.toLowerCase().endsWith(ext))

function MediaFrame({ url, storyLike }) {
  if (!url) {
    return (
      <div
        className="bg-light d-flex align-items-center justify-content-center"
        style={{ width: 300, height: 300, color: '#999' }}
      >
        No media
      </div>
    )
  }

  const video = isVideoUrl(url)

  if (storyLike) {
    return (
      <div
        style={{
          width: 180,
          height: 320,
          backgroundColor: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto',
        }}
      >
        {video ? (
          <video src={url} controls muted style={{ maxWidth: '100%', maxHeight: '100%' }} />
        ) : (
          <img src={url} alt="ig-story" style={{ maxWidth: '100%', maxHeight: '100%' }} />
        )}
      </div>
    )
  }

  // Feed/carousel square
  const mediaStyle = { width: 300, height: 300, objectFit: 'cover' }
  return video ? (
    <video src={url} controls style={mediaStyle} />
  ) : (
    <img src={url} alt="ig-media" style={mediaStyle} />
  )
}

export default function InstagramPreview({
  account,
  files,
  caption,
  postType,
  scheduleDateTime,
}) {
  const format      = account?.options?.format || 'feed'
  const storyLike   = format === 'story' || format === 'reel'
  const [carouselIdx, setCarouselIdx] = useState(0)
  const hasCarousel = format === 'carousel' || files.length > 1
  const main        = files.length ? files[carouselIdx]?.url : null
  const avatar      =
    account?.userAvatar || account?.profileImage || account?.avatarUrl || PLACEHOLDER
  const displayName = account?.displayName || 'instagram_user'

  // ------------- Story / Reel ---------------
  if (storyLike) {
    return (
      <div
        className="border rounded bg-white p-2 mb-3"
        style={{ width: 200, fontFamily: 'Helvetica, Arial, sans-serif' }}
      >
        <div className="d-flex align-items-center mb-1">
          <img
            src={avatar}
            alt="avatar"
            className="rounded-circle"
            style={{ width: 24, height: 24, objectFit: 'cover' }}
          />
          <strong className="ms-2" style={{ fontSize: 12 }}>
            {displayName}
          </strong>
          <span className="ms-auto" style={{ fontSize: 10, color: '#888' }}>
            {postType === 'schedule' && scheduleDateTime
              ? new Date(scheduleDateTime).toLocaleString()
              : 'Just now'}
          </span>
        </div>
        <MediaFrame url={main} storyLike />
      </div>
    )
  }

  // ------------- Feed / Carousel ---------------
  return (
    <div
      className="border rounded bg-white mb-3"
      style={{ width: 300, fontFamily: 'Helvetica, Arial, sans-serif' }}
    >
      {/* header */}
      <div className="d-flex align-items-center p-2">
        <img
          src={avatar}
          alt="avatar"
          className="rounded-circle"
          style={{ width: 32, height: 32, objectFit: 'cover' }}
        />
        <strong className="ms-2">{displayName}</strong>
        <i className="fas fa-ellipsis-h ms-auto" />
      </div>

      {/* media carousel */}
      <div style={{ position: 'relative', width: 300, height: 300, background: '#000' }}>
        <MediaFrame url={main} storyLike={false} />
        {hasCarousel && files.length > 1 && (
          <>
            {/* Left arrow */}
            <button
              type="button"
              className="btn btn-light"
              disabled={carouselIdx === 0}
              style={{
                position: 'absolute',
                top: '50%',
                left: 5,
                transform: 'translateY(-50%)',
                opacity: carouselIdx === 0 ? 0.4 : 0.9,
                zIndex: 2,
                padding: 0,
                width: 32,
                height: 32,
                borderRadius: '50%',
                fontWeight: 'bold',
              }}
              onClick={() => setCarouselIdx(i => Math.max(i - 1, 0))}
            >
              <i className="fas fa-chevron-left" />
            </button>
            {/* Right arrow */}
            <button
              type="button"
              className="btn btn-light"
              disabled={carouselIdx === files.length - 1}
              style={{
                position: 'absolute',
                top: '50%',
                right: 5,
                transform: 'translateY(-50%)',
                opacity: carouselIdx === files.length - 1 ? 0.4 : 0.9,
                zIndex: 2,
                padding: 0,
                width: 32,
                height: 32,
                borderRadius: '50%',
                fontWeight: 'bold',
              }}
              onClick={() => setCarouselIdx(i => Math.min(i + 1, files.length - 1))}
            >
              <i className="fas fa-chevron-right" />
            </button>
            {/* Carousel dots */}
            <div style={{
              position: 'absolute',
              bottom: 8,
              width: '100%',
              textAlign: 'center',
              zIndex: 2
            }}>
              {files.map((_, idx) => (
                <span
                  key={idx}
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    margin: '0 2px',
                    borderRadius: '50%',
                    background: idx === carouselIdx ? '#3897f0' : '#ccc'
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* actions */}
      <div className="d-flex align-items-center p-1">
        <i className="far fa-heart me-2" />
        <i className="far fa-comment me-2" />
        <i className="far fa-paper-plane" />
      </div>

      {/* caption */}
      {caption && (
        <div className="px-2 pb-2" style={{ fontSize: 14 }}>
          <strong>{displayName}</strong>{' '}
          <span style={{ whiteSpace: 'pre-wrap' }}>{caption}</span>
        </div>
      )}

      {/* time */}
      <div className="px-2 pb-2" style={{ fontSize: 10, color: '#888' }}>
        {postType === 'schedule' && scheduleDateTime
          ? `Scheduled • ${new Date(scheduleDateTime).toLocaleString()}`
          : 'Just now'}
      </div>
    </div>
  )
}
